import classNames from 'classnames';
import { FC, useRef } from 'react';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'src/app/hooks';
import { FormInput } from 'src/components/ui/FormInput/FormInput';
import { FormPhoneInput } from 'src/components/ui/FormPhoneInput/FormPhoneInput';
import { HTMLCountrySelectElement } from 'src/components/ui/PhoneInput/PhoneInput';
import { formRules, maxLengthRules } from 'src/constants/formConstants';
import { addCompany, editCompany } from 'src/store/actions/companyActions';
import { Company } from 'src/store/models/Company';
import countries from 'src/utils/countries';
import { useCountryPhoneSelect } from 'src/utils/useCountryPhoneSelect';
import { useErrorSelector } from 'src/utils/useLoadingSelector';

import classes from '../CompanyDetails.module.scss';
import CompanyDetailsFormSkeleton from './CompanyDetailsFormSkeleton';

export interface CompanyDetailsFormProps {
  loading?: boolean;
  edit?: boolean;
}

const CompanyDetailsForm: FC<CompanyDetailsFormProps> = ({
  loading = false,
  edit = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const phoneSelectRef = useRef<HTMLCountrySelectElement>(null);

  const createError = useErrorSelector<Company>({
    actionType: edit ? editCompany.toString() : addCompany.toString(),
  });

  const { control, watch, handleSubmit } = useFormContext<Company>();
  const notes = watch('notes');
  const country = watch('country');

  useCountryPhoneSelect(country, phoneSelectRef?.current);

  const onSubmit: SubmitHandler<Company> = (formData) => {
    if (edit) {
      dispatch(editCompany(formData));
    } else {
      dispatch(addCompany(formData));
    }
  };

  return (
    <form
      noValidate
      className={classNames({ [classes.Loading]: loading })}
      id="new-company-form"
      onSubmit={handleSubmit(onSubmit)}
    >
      {!loading ? (
        <>
          <h4 className={classNames({ [classes.Edit]: edit })}>
            {t('service_account:company_info')}
          </h4>
          {edit && <p>{t('service_account:company_info_changes')}</p>}
          <section>
            <FormInput<Company>
              control={control}
              name="title"
              placeholder={t('service_account:enter_company_name')}
              label={t('service_account:company_name')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!createError?.title}
              helperText={createError?.title}
            />
            <FormInput<Company>
              control={control}
              name="line1"
              placeholder={t('common:enter_street_name')}
              label={t('common:address_line_1')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!createError?.line1}
              helperText={createError?.line1}
            />
            <FormInput<Company>
              control={control}
              name="line2"
              placeholder={t('common:enter_additional_address')}
              label={`${t('common:address_line_2')} (${t(
                'common:optional'
              ).toLowerCase()})`}
              rules={maxLengthRules()}
              error={!!createError?.line2}
              helperText={createError?.line2}
            />
            <FormInput<Company>
              control={control}
              name="zip"
              placeholder={t('common:enter_postcode')}
              label={t('common:zip_postcode')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!createError?.zip}
              helperText={createError?.zip}
            />
            <FormInput<Company>
              control={control}
              name="city"
              placeholder={t('common:enter_town_city')}
              label={t('common:city')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!createError?.city}
              helperText={createError?.city}
            />
            <FormInput<Company>
              control={control}
              name="country"
              label={t('common:country')}
              rules={formRules}
              inputProps={{
                select: true,
                children: (
                  <>
                    <option disabled value={''}>{`${t(
                      'common:choose'
                    )}...`}</option>
                    {Object.keys(countries?.getNames('en'))?.map((v, i) => (
                      <option key={i} value={v}>
                        {countries?.getNames('en')?.[v]}
                      </option>
                    ))}
                  </>
                ),
              }}
              error={!!createError?.country}
              helperText={createError?.country}
            />
            <FormInput<Company>
              control={control}
              name="eMail"
              type="email"
              placeholder={t('common:enter_email')}
              label={t('common:email')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!createError?.eMail}
              helperText={createError?.eMail}
            />
            <FormPhoneInput<Company>
              control={control}
              name="phone"
              placeholder={t('common:enter_phone')}
              label={t('common:phone')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!createError?.phone}
              helperText={createError?.phone}
              selectRef={phoneSelectRef}
            />
          </section>
          <h4>{t('service_account:other_information')}</h4>
          <FormInput<Company>
            control={control}
            name="notes"
            placeholder={t('service_account:company_notes')}
            label={`${t('common:notes')} (${t(
              'common:optional'
            ).toLowerCase()})`}
            inputProps={{
              fullWidth: true,
              multiline: true,
              rows: 5,
              inputProps: { maxLength: 500 },
            }}
            className={classes.Notes}
            error={!!createError?.notes}
            helperText={`${notes?.length || 0}/500`}
          />
        </>
      ) : (
        <CompanyDetailsFormSkeleton />
      )}
    </form>
  );
};

export default CompanyDetailsForm;
